import { Check, Close } from 'assets/icons';
import UseContext from 'hooks/useContext';
import { fillText, listImportLabel } from './invoiceImportText';

const ratio = (part, whole) =>
  whole > 0 ? Math.min(100, Math.round((part / whole) * 100)) : 0;

const Spinner = () => (
  <svg
    className="animate-spin shrink-0"
    width="32"
    height="32"
    viewBox="0 0 32 32"
    aria-hidden="true"
  >
    <circle
      cx="16"
      cy="16"
      r="13"
      fill="none"
      stroke="#374151"
      strokeWidth="4"
    />
    <path
      d="M16 3a13 13 0 0 1 13 13"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);

const Badge = ({ success }) => (
  <div
    className={`w-8 h-8 shrink-0 rounded-md flex items-center justify-center ${
      success ? 'bg-green-900' : 'bg-red-900'
    }`}
  >
    {success ? (
      <Check wh={16} color="#4ADE80" />
    ) : (
      <Close wh={16} color="#F87171" />
    )}
  </div>
);

/**
 * Progress of an invoice import (JBWEB-1266, Figma "Toast/Import factures"):
 * BeSTT then Pennylane, then the outcome. Counts come from the run as the api
 * gives them. A run that ends with invoices in error offers "Voir les
 * erreurs", which opens the import journal (JBWEB-1280). The import of a list
 * says so above its title (JBWEB-1274), never read as an import of the month.
 */
const InvoiceImportToast = ({ run, onClose, onShowErrors }) => {
  const { language } = UseContext();
  const text = (key, fallback) =>
    language.getValue(`invoiceImport.${key}`, fallback);
  if (!run) return null;

  const bestt = run.counters?.bestt ?? {};
  const pennylane = run.counters?.pennylane ?? {};
  const total = bestt.total ?? 0;
  const errors = (bestt.failed ?? 0) + (pennylane.failed ?? 0);
  const listLabel = listImportLabel(text, run);

  let icon = <Spinner />;
  let title;
  let line;
  let bar = null;
  let showErrors = false;

  if (run.status === 'running' && run.step === 'pennylane') {
    title = text('stepPennylane', 'Étape 2/2 · Import dans Pennylane');
    line = fillText(
      text('importedCount', '{done} / {total} factures importées'),
      {
        done: pennylane.done ?? 0,
        total: pennylane.total ?? 0,
      },
    );
    bar = [
      {
        width: ratio(
          (pennylane.done ?? 0) + (pennylane.failed ?? 0),
          pennylane.total ?? 0,
        ),
        color: 'bg-white',
      },
    ];
  } else if (run.status === 'running') {
    title = text('stepBestt', 'Étape 1/2 · Récupération depuis Bestt');
    line = fillText(
      text('fetchedCount', '{done} / {total} factures récupérées'),
      {
        done: bestt.done ?? 0,
        total,
      },
    );
    bar = [
      {
        width: ratio((bestt.done ?? 0) + (bestt.failed ?? 0), total),
        color: 'bg-white',
      },
    ];
  } else if (run.status === 'failed') {
    icon = <Badge success={false} />;
    title = text('failed', 'Import impossible');
    line = text(
      `fatal.${run.fatalCode || 'generic'}`,
      run.fatalCode === 'ERR_BESTT_INDISPONIBLE'
        ? 'Bestt ne répond pas : rien n’a été importé. Réessayez plus tard.'
        : run.fatalCode === 'ERR_PASSAGE_INTERROMPU'
          ? 'L’import s’est interrompu. Réessayez : rien ne sera importé en double.'
          : 'L’import a échoué. Réessayez.',
    );
  } else if (errors > 0) {
    icon = <Badge success={false} />;
    title = text('finishedWithErrors', 'Import terminé avec des erreurs');
    line = fillText(
      text(
        'finishedWithErrorsCount',
        '{done} / {total} importées · {errors} en erreur',
      ),
      {
        done: pennylane.done ?? 0,
        total,
        errors,
      },
    );
    bar = [
      { width: ratio(pennylane.done ?? 0, total), color: 'bg-green-500' },
      { width: ratio(errors, total), color: 'bg-red-500' },
    ];
    showErrors = Boolean(onShowErrors);
  } else {
    icon = <Badge success />;
    title = text('finished', 'Import terminé');
    line = fillText(
      text('importedCount', '{done} / {total} factures importées'),
      {
        done: pennylane.done ?? 0,
        total,
      },
    );
    bar = [{ width: ratio(pennylane.done ?? 0, total), color: 'bg-green-500' }];
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-4 z-[60] w-[380px] rounded-lg bg-gray-900 text-white shadow-lg p-4 flex flex-col gap-3"
    >
      <div className="flex items-start gap-3">
        {icon}
        <div className="flex-1 min-w-0">
          {listLabel && (
            <div className="text-xs text-gray-400">{listLabel}</div>
          )}
          <div className="text-sm font-medium">{title}</div>
          <div className="text-sm text-gray-400">{line}</div>
        </div>
        <button
          type="button"
          aria-label={text('close', 'Fermer')}
          onClick={onClose}
          className="shrink-0"
        >
          <Close wh={16} color="#9CA3AF" />
        </button>
      </div>
      {bar && (
        <div
          className="flex h-1.5 w-full overflow-hidden rounded-full bg-gray-700"
          data-testid="invoice-import-progress"
        >
          {bar.map((segment, index) => (
            <div
              key={index}
              className={`${segment.color} motion-safe:transition-[width] motion-safe:duration-500 motion-safe:ease-out`}
              style={{ width: `${segment.width}%` }}
            />
          ))}
        </div>
      )}
      {showErrors && (
        <button
          type="button"
          onClick={onShowErrors}
          className="self-start text-sm font-medium text-white underline"
        >
          {text('showErrors', 'Voir les erreurs')}
        </button>
      )}
    </div>
  );
};

export default InvoiceImportToast;
